import PropTypes from 'prop-types';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { Icon } from '@iconify/react';

const ICON_MAP = {
  'fault connectors': 'solar:plug-circle-bold-duotone',
  'preparing connectors': 'solar:cpu-bolt-bold-duotone',
  'active charging sessions': 'solar:radar-2-bold-duotone',
  'charging sessions': 'solar:graph-new-bold-duotone',
  'total revenue': 'solar:wallet-money-bold-duotone',
  'new users': 'solar:user-id-bold-duotone',
  'new charge points': 'solar:charging-station-bold-duotone',
  'total energy': 'solar:flash-circle-bold-duotone',
  default: 'solar:chart-line-bold-duotone'
};

const getIconForTitle = (title) => {
  if (!title) return ICON_MAP.default;
  const key = title.toLowerCase();
  return ICON_MAP[key] || ICON_MAP.default;
};

const MetricStatCard = ({
  title,
  value,
  secondaryText,
  badgeText,
  trendText,
  trendDirection = 'down',
  accent = 'secondary',
  icon,
  isLoading = false
}) => {
  const theme = useTheme();
  const paletteTokens = theme.palette.metric || {};
  const accentTokens = paletteTokens.accent || {};
  const accentColor = accentTokens[accent] || accentTokens.secondary || '#3b82f6';
  const cardBg = paletteTokens.card || (theme.palette.mode === 'light' ? '#ffffff' : '#111c2d');
  const cardBorder = paletteTokens.border || (theme.palette.mode === 'light' ? '#e2e8f0' : '#1f2937');
  const labelColor = paletteTokens.label || (theme.palette.mode === 'light' ? '#475569' : '#94a3b8');
  const headlineColor = paletteTokens.headline || (theme.palette.mode === 'light' ? '#0f172a' : '#f1f5f9');
  const secondaryColor = paletteTokens.secondary || (theme.palette.mode === 'light' ? '#475569' : '#9ca3af');
  const cardShadow = paletteTokens.shadow || (theme.palette.mode === 'light' ? '0 15px 35px rgba(15,23,42,0.08)' : '0 30px 60px rgba(2,6,23,0.45)');
  const iconSurface = paletteTokens.iconSurface || (theme.palette.mode === 'light' ? 'rgba(15,23,42,0.06)' : 'rgba(148,163,184,0.08)');
  const badgeTokens = paletteTokens.badges?.live || {
    background: theme.palette.mode === 'light' ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.15)',
    text: '#4ade80',
    border: theme.palette.mode === 'light' ? 'rgba(34,197,94,0.4)' : 'rgba(34,197,94,0.35)'
  };
  const buildTrendTokens = (direction) => {
    const isPositive = direction === 'up';
    const successBase = theme.palette.success.main;
    const warningBase = theme.palette.warning.main;
    if (isPositive) {
      return {
        background: alpha(successBase, theme.palette.mode === 'light' ? 0.12 : 0.28),
        border: alpha(successBase, theme.palette.mode === 'light' ? 0.4 : 0.6),
        text:
          theme.palette.mode === 'light'
            ? theme.palette.success.darker || '#166534'
            : theme.palette.success.light || '#86efac'
      };
    }
    const fallbackTrend = paletteTokens.trend || {
      background: theme.palette.mode === 'light' ? '#fef3c7' : '#3a2e0f',
      border: theme.palette.mode === 'light' ? '#fbbf24' : '#5a4516',
      text: theme.palette.mode === 'light' ? '#92400e' : '#fbbf24'
    };
    return {
      background:
        paletteTokens.trend?.background || alpha(warningBase, theme.palette.mode === 'light' ? 0.18 : 0.32),
      border:
        paletteTokens.trend?.border || alpha(warningBase, theme.palette.mode === 'light' ? 0.45 : 0.65),
      text: paletteTokens.trend?.text || fallbackTrend.text
    };
  };
  const trendTokens = buildTrendTokens(trendDirection);

  const iconName = icon || getIconForTitle(title);
  const showTrend = Boolean(trendText && trendText.trim().length > 0);
  const TrendIcon = trendDirection === 'up' ? TrendingUpRoundedIcon : TrendingDownRoundedIcon;
  const hasSecondary =
    secondaryText !== undefined &&
    secondaryText !== null &&
    `${secondaryText}`.toString().trim().length > 0;

  return (
    <Box
      sx={{
        backgroundColor: cardBg,
        borderRadius: '16px',
        border: `1px solid ${cardBorder}`,
        padding: theme.spacing(2.5),
        minHeight: 190,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        boxShadow: cardShadow,
        height: '100%'
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Stack spacing={1} flex={1} minWidth={0}>
          <Typography
            variant="subtitle2"
            sx={{
              color: labelColor,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontSize: 12,
              fontWeight: 600
            }}
          >
            {title}
          </Typography>
          {isLoading ? (
            <>
              <Skeleton width="60%" height={38} animation="wave" sx={{ borderRadius: 1 }} />
              <Skeleton width="40%" height={20} animation="wave" sx={{ borderRadius: 1 }} />
            </>
          ) : (
            <>
              <Typography
                variant="h3"
                sx={{
                  color: headlineColor,
                  fontWeight: 600,
                  fontSize: 36,
                  lineHeight: 1.1,
                  wordBreak: 'break-all'
                }}
              >
                {value ?? '—'}
              </Typography>
              {hasSecondary && (
                <Typography variant="body2" sx={{ color: secondaryColor, fontWeight: 500 }}>
                  {secondaryText}
                </Typography>
              )}
            </>
          )}
        </Stack>
        <Stack spacing={1} alignItems="flex-end">
          {badgeText && (
            <Box
              sx={{
                backgroundColor: badgeTokens.background,
                border: `1px solid ${badgeTokens.border}`,
                color: badgeTokens.text,
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                px: 1.5,
                py: 0.25,
                letterSpacing: '0.06em'
              }}
            >
              {badgeText}
            </Box>
          )}
          <Box
            sx={{
              backgroundColor: iconSurface,
              borderRadius: '14px',
              padding: theme.spacing(1.25),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon
              icon={iconName}
              width={28}
              height={28}
              color={accentColor}
              style={{ opacity: 0.85 }}
            />
          </Box>
        </Stack>
      </Stack>

      {showTrend && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            marginTop: 'auto',
            backgroundColor: trendTokens.background,
            border: `1px solid ${trendTokens.border}`,
            color: trendTokens.text,
            borderRadius: 12,
            padding: theme.spacing(0.75, 1.5)
          }}
        >
          <TrendIcon sx={{ color: trendTokens.text, fontSize: 22 }} />
          <Typography
            variant="body2"
            sx={{
              color: trendTokens.text,
              fontWeight: 600,
              letterSpacing: 0.2,
              textTransform: 'capitalize'
            }}
          >
            {trendText}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

MetricStatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  secondaryText: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  badgeText: PropTypes.string,
  trendText: PropTypes.string,
  trendDirection: PropTypes.oneOf(['up', 'down']),
  accent: PropTypes.string,
  icon: PropTypes.string,
  isLoading: PropTypes.bool
};

export default MetricStatCard;
